import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Breadcrumb, Icon, Pagination, PastTripsCard, SectionHeader } from "@zorni/components-lib";
import styles from "./MyTrips.module.css";
import { Link, useNavigate } from "react-router-dom";
import { getTravelerCount, useReservationList } from "./hooks/useReservationList ";
import { useZorni } from "../../../hooks/useZorni";
import { useNotifications } from "../../../hooks/NotificationContext";
import { useReviewedPublicationIds } from "./hooks/useReviewedPublicationIds";
import UpcomingTripCard from "./upcomingTripCard/UpcomingTripCard";
import { currentLocale } from "../../../../common/i18n/locale";

const TABS = ["upcoming", "past"];
const UPCOMING_STATUS = "CONFIRMED";
const PAST_STATUS = "PASSED";

const normalizeStatus = (status) => String(status ?? "").trim().toUpperCase();
const resolveCopy = (t, key, fallback) => {
  const value = t(key);
  return !value || value === key ? fallback : value;
};
const getTrip = (reservation) => reservation?.tripPublicationResume?.trip;
const getMainPicture = (reservation) =>
  getTrip(reservation)?.pictures?.find((picture) => picture?.isMain)?.link ||
  getTrip(reservation)?.pictures?.[0]?.link ||
  "";
const getTripCountry = (reservation) =>
  getTrip(reservation)?.tripProgramDetails?.[0]?.country || "";
const getAgencyName = (reservation) =>
  getTrip(reservation)?.establishment?.company?.legalName || getTrip(reservation)?.title || "";
const formatCurrencyCode = (currency) => {
  switch (String(currency ?? "").trim().toUpperCase()) {
    case "EUR":
      return "€";
    case "USD":
      return "$";
    default:
      return String(currency ?? "").trim();
  }
};
const getTravelTotalPriceLabel = (reservation) => {
  const totalPrice = reservation?.pricingDetails?.travelTotalPrice;
  const value = totalPrice?.value;
  const currency = String(totalPrice?.currency ?? "").trim();

  if (value === null || value === undefined || value === "") return "";
  return currency ? `${value} ${formatCurrencyCode(currency)}` : String(value);
};
const toTimestamp = (value, fallback) => {
  const date = new Date(value);
  const time = date.getTime();
  return Number.isNaN(time) ? fallback : time;
};

const MyTrips = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userId, isAuthenticated } = useZorni();
  const { hasNewReservations, clearReservations } = useNotifications();

  const [currentPage, setCurrentPage] = useState(1);
  const [tab, setTab] = useState("upcoming");
  const [query, setQuery] = useState("");
  const elementsPerPage = 9;

  // 🧹 Consulter "Mes voyages" traite les notifications de réservation non lues (indépendant
  // des notifications de type message) — n'est PAS déclenché par la seule ouverture du header.
  useEffect(() => {
    if (hasNewReservations) {
      clearReservations();
    }
  }, [hasNewReservations, clearReservations]);

  const { list: reservationList = [], loading } = useReservationList({
    paginationParams: {
      currentPage: 0,
      sizePage: 1000,
    },
    tripPublicationId: null,
    ownerId: userId,
    skip: !isAuthenticated || !userId,
  });

  const upcomingReservations = useMemo(() => {
    return reservationList
      .filter((reservation) => normalizeStatus(reservation?.status) === UPCOMING_STATUS)
      .sort(
        (a, b) =>
          toTimestamp(a?.tripDate?.startDate, Number.POSITIVE_INFINITY) -
          toTimestamp(b?.tripDate?.startDate, Number.POSITIVE_INFINITY)
      );
  }, [reservationList]);

  const pastReservations = useMemo(() => {
    return reservationList
      .filter((reservation) => normalizeStatus(reservation?.status) === PAST_STATUS)
      .sort(
        (a, b) =>
          toTimestamp(b?.tripDate?.endDate, Number.NEGATIVE_INFINITY) -
          toTimestamp(a?.tripDate?.endDate, Number.NEGATIVE_INFINITY)
      );
  }, [reservationList]);

  /* Recherche : titre du voyage, agence ou pays. */
  const matches = (reservation) => {
    const needle = query.trim().toLowerCase();
    if (!needle) return true;
    return [getTrip(reservation)?.title, getAgencyName(reservation), getTripCountry(reservation)]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(needle));
  };

  const upcomingShown = useMemo(
    () => upcomingReservations.filter(matches),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [upcomingReservations, query]
  );

  const pastShown = useMemo(
    () => pastReservations.filter(matches),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pastReservations, query]
  );

  const countriesVisited = useMemo(
    () =>
      new Set(
        pastReservations.map((reservation) => getTripCountry(reservation)).filter(Boolean)
      ).size,
    [pastReservations]
  );

  const totalPages = useMemo(() => {
    const pages = Math.ceil(pastShown.length / elementsPerPage);
    return pages > 0 ? pages : 1;
  }, [pastShown.length, elementsPerPage]);

  const pagedPastReservations = useMemo(() => {
    const start = (currentPage - 1) * elementsPerPage;
    return pastShown.slice(start, start + elementsPerPage);
  }, [pastShown, currentPage, elementsPerPage]);

  /* Le hook interroge l'API une fois par publication : on ne lui donne que
     les voyages réellement affichés, pas l'historique entier. Trente voyages
     passés déclenchaient trente requêtes pour neuf cartes. */
  const pastPublicationIds = useMemo(
    () => pagedPastReservations.map((reservation) => reservation?.publicationId),
    [pagedPastReservations]
  );

  const { reviewedPublicationIds, reviewedMarks, loading: reviewsLoading } = useReviewedPublicationIds({
    publicationIds: pastPublicationIds,
    userId,
    enabled: isAuthenticated && Boolean(userId) && pastReservations.length > 0,
  });

  // Navigation
  const handleLeaveReview = (reservation) => {
    navigate(`/${reservation.id}/review`);
  };

  /* Pied de la carte « voyage passé ». Deux actions dans les deux états, pour
     que les cartes d'une même ligne restent alignées : une fois l'avis donné,
     le bouton principal cédait la place à un bloc vide. */
  const pastCardActions = (reservation) => {
    const publicationId = reservation?.publicationId;
    const alreadyReviewed =
      Boolean(publicationId) &&
      reviewedPublicationIds.has(String(publicationId));
    const tripDateId = reservation?.tripDate?.id;

    return [
      !alreadyReviewed &&
        Boolean(publicationId) && {
          key: "review",
          label: resolveCopy(
            t,
            "components.pastTrip.review",
            "Laisser un avis"
          ),
          iconName: "iconPenToSquare",
          variant: "primary",
          disabled: reviewsLoading,
          onClick: () => handleLeaveReview(reservation),
        },
      {
        key: "recap",
        label: t("myTrips.pastActions.recap", "Voir le récap"),
        iconName: "iconFolderClosed",
        onClick: () => navigate(`/reservation/${reservation.id}`),
      },
      /* Reproposer le voyage une fois l'avis donné. La page détail garde son
         sélecteur de dates : le voyageur y choisit un départ à venir. */
      alreadyReviewed &&
        Boolean(publicationId) &&
        Boolean(tripDateId) && {
          key: "rebook",
          label: t("myTrips.pastActions.rebook", "Réserver à nouveau"),
          iconName: "iconCalendarCheck",
          onClick: () => navigate(`/trips/${publicationId}/${tripDateId}`),
        },
    ].filter(Boolean);
  };

  // Reset page if page size changes or if current page is out of range
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const title = resolveCopy(t, "components.myTrips.pageTitle", "Mes voyages");
  const hello = resolveCopy(
    t,
    "myTrips.hello",
    "Retrouvez vos prochains départs, vos réservations passées et vos avis."
  );

  const stats = [
    { icon: "iconCalendarCheck", value: upcomingReservations.length, label: t("myTrips.statUpcoming", "Voyages à venir") },
    { icon: "iconArchive", value: pastReservations.length, label: t("myTrips.statPast", "Voyages terminés") },
    { icon: "iconGlobe", value: countriesVisited, label: t("myTrips.statCountries", "Pays visités") },
  ];

  const onTab = (next) => {
    setTab(next);
    setCurrentPage(1);
  };

  const onTabKeyDown = (event) => {
    const index = TABS.indexOf(tab);
    let suivant = null;

    if (event.key === "ArrowRight" || event.key === "ArrowDown") {
      suivant = TABS[(index + 1) % TABS.length];
    } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
      suivant = TABS[(index - 1 + TABS.length) % TABS.length];
    } else if (event.key === "Home") {
      suivant = TABS[0];
    } else if (event.key === "End") {
      suivant = TABS[TABS.length - 1];
    }

    if (!suivant) return;

    event.preventDefault();
    onTab(suivant);
    document.getElementById(`tab-${suivant}`)?.focus();
  };

  const emptyState = (message) => (
    <div className={styles.empty}>
      <i aria-hidden="true">
        <Icon name="iconSuitcase" size="lg" />
      </i>
      <p>{message}</p>
      {tab === "upcoming" && !query && (
        <button type="button" className={styles.exploreBtn} onClick={() => navigate("/trips")}>
          {t("myTrips.explore", "Explorer les voyages")}
          <Icon name="iconArrowRight" size="sm" />
        </button>
      )}
    </div>
  );

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        {/* Fil d'Ariane commun aux pages du compte (favoris, profil). */}
        <Breadcrumb
          className={styles.breadcrumb}
          ariaLabel={t("trips.results.breadcrumb", "Fil d'Ariane")}
          linkComponent={Link}
          items={[
            { label: t("trips.results.home", "Accueil"), to: "/" },
            { label: t("components.myTrips.pageTitle", "Mes voyages") },
          ]}
        />

        <header className={styles.head}>
          <SectionHeader
            size="page"
            kicker={t("myTrips.kicker", "Espace client")}
            title={title}
            subtitle={hello}
          />
          <label className={styles.search}>
            <Icon name="iconSearch" size="sm" />
            <input
              type="search"
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setCurrentPage(1);
              }}
              placeholder={t("myTrips.search", "Rechercher un voyage")}
              aria-label={t("myTrips.search", "Rechercher un voyage")}
            />
          </label>
        </header>

        <section className={styles.stats} aria-label={t("myTrips.activity", "Votre activité")}>
          {stats.map((stat) => (
            <div key={stat.label} className={styles.stat}>
              <i aria-hidden="true">
                <Icon name={stat.icon} size="md" />
              </i>
              <span>
                <b>{stat.value}</b>
                <small>{stat.label}</small>
              </span>
            </div>
          ))}
        </section>

        <div className={styles.tabs} role="tablist" aria-label={t("myTrips.tabsLabel", "Vos voyages")}>
          {TABS.map((key) => {
            const selected = tab === key;

            return (
              <button
                key={key}
                type="button"
                role="tab"
                id={`tab-${key}`}
                aria-controls={`panel-${key}`}
                aria-selected={selected}
                /* Un seul onglet reste atteignable au Tab : à l'intérieur du
                   groupe, on circule aux flèches, comme l'attend un lecteur
                   d'écran. */
                tabIndex={selected ? 0 : -1}
                className={styles.tab}
                onClick={() => onTab(key)}
                onKeyDown={onTabKeyDown}
              >
                {key === "upcoming"
                  ? t("myTrips.tabUpcoming", "Voyages à venir")
                  : t("myTrips.tabPast", "Voyages passés")}
                <b>{key === "upcoming" ? upcomingShown.length : pastShown.length}</b>
              </button>
            );
          })}
        </div>

        {tab === "upcoming" && (
          <section
            className={styles.upcoming}
            role="tabpanel"
            id="panel-upcoming"
            aria-labelledby="tab-upcoming"
          >
            {!loading && upcomingShown.map((reservation) => (
              <UpcomingTripCard
                key={`upcoming-${reservation?.id}`}
                reference={String(reservation?.id ?? "").slice(0, 8).toUpperCase()}
                picture={getMainPicture(reservation)}
                title={getTrip(reservation)?.title}
                location={getTripCountry(reservation)}
                status={normalizeStatus(reservation?.status)}
                nights={reservation?.tripDate?.nbNight}
                travelers={getTravelerCount(reservation?.reservedPlaces)}
                price={getTravelTotalPriceLabel(reservation)}
                startDate={reservation?.tripDate?.startDate}
                endDate={reservation?.tripDate?.endDate}
                onSeeReservation={() => navigate(`/reservation/${reservation.id}`)}
                onContactAgency={() => navigate("/messenger")}
              />
            ))}

            {!loading && upcomingShown.length === 0 &&
              emptyState(
                query
                  ? t("myTrips.noMatch", "Aucun voyage ne correspond à votre recherche.")
                  : resolveCopy(
                      t,
                      "components.myTrips.noUpcomingReservations",
                      "Aucun voyage à venir pour le moment."
                    )
              )}
          </section>
        )}

        {tab === "past" && (
          <section role="tabpanel" id="panel-past" aria-labelledby="tab-past">
            <div className={styles.past}>
              {!loading && pagedPastReservations.map((reservation) => (
                <PastTripsCard
                  key={`past-${reservation?.id}`}
                  t={t}
                  locale={currentLocale()}
                  thumbnail={getMainPicture(reservation)}
                  title={getTrip(reservation)?.title || getAgencyName(reservation)}
                  country={getTripCountry(reservation)}
                  startDate={reservation?.tripDate?.startDate}
                  endDate={reservation?.tripDate?.endDate}
                  /* Déjà noté : on montre la note et on retire le bouton, plutôt
                     qu'un bouton grisé sous un « Pas encore d'avis » trompeur. */
                  rating={reviewedMarks[String(reservation?.publicationId)]}
                  actions={pastCardActions(reservation)}
                />
              ))}
            </div>

            {!loading && pastShown.length === 0 &&
              emptyState(
                query
                  ? t("myTrips.noMatch", "Aucun voyage ne correspond à votre recherche.")
                  : resolveCopy(
                      t,
                      "components.myTrips.noPastDestinations",
                      "Aucun voyage passé pour le moment."
                    )
              )}

            {totalPages > 1 && (
              <div className={styles.paginationWrap}>
                <Pagination
                  totalPages={totalPages}
                  currentPage={currentPage}
                  onPageChange={setCurrentPage}
                />
              </div>
            )}
          </section>
        )}

        <aside className={styles.help}>
          <div>
            <h3>{t("myTrips.helpTitle", "Une question sur un voyage ?")}</h3>
            <p>
              {t(
                "myTrips.helpText",
                "Votre agence répond sur les options, les documents et les modalités de départ."
              )}
            </p>
          </div>
          <button type="button" className={styles.helpBtn} onClick={() => navigate("/messenger")}>
            <Icon name="iconEnvelope" size="sm" />
            {t("myTrips.contactAgency", "Contacter l'agence")}
          </button>
        </aside>
      </div>
    </div>
  );
};

export default MyTrips;
